import { createContext, memo, type ReactNode, useContext, useState } from 'react';
import { useIsomorphicLayoutEffect } from 'react-use';

const ShowcaseEditorCoveringContext = createContext(false);
const SetShowcaseEditorCoveringContext = createContext<(covering: boolean) => void>(() => undefined);

export type ShowcaseEditorCoverProviderProps = {
  children: ReactNode;
};

/**
 * The showcases page mounts the editor from `?showcase=` before that editor has painted.
 * Consumers read `covering` so the list stays the main until the overlay is actually up.
 */
export const ShowcaseEditorCoverProvider = memo(({ children }: ShowcaseEditorCoverProviderProps) => {
  const [covering, setCovering] = useState(false);

  return (
    <SetShowcaseEditorCoveringContext.Provider value={setCovering}>
      <ShowcaseEditorCoveringContext.Provider value={covering}>{children}</ShowcaseEditorCoveringContext.Provider>
    </SetShowcaseEditorCoveringContext.Provider>
  );
});

ShowcaseEditorCoverProvider.displayName = 'ShowcaseEditorCoverProvider';

export const useShowcaseEditorCovering = () => useContext(ShowcaseEditorCoveringContext);

/** Marks the shell covered for as long as this component is mounted. */
export const useCoverShellWhileMounted = () => {
  const setCovering = useContext(SetShowcaseEditorCoveringContext);

  useIsomorphicLayoutEffect(() => {
    setCovering(true);

    return () => setCovering(false);
  }, [setCovering]);
};
